import React, { useState, useRef, useCallback, useEffect } from 'react';
import AccountMenu from './AccountMenu';
import FullFeaturedModal from './FullFeaturedModal';
import { ValetIcon } from './icons/ValetIcon';
import { QobuzIcon, PrestoMusicIcon, EllaRadioIcon, GramophoneIcon } from './ServiceIcons';
import { useDesktopMode, useMaestroStore } from '../store/maestroStore';
import maestroLogo from '../assets/icons/Maestro-Logo.png';
import useSidebarConfig from '../hooks/useSidebarConfig';
import useSidebarOrder from '../hooks/useSidebarOrder';
import { playlistService } from '../services/playlistService';
import { useTranslation } from '../i18n/useTranslation';
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  closestCenter,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const NavIcon = ({ children, active }) => (
  <span
    className="w-5 h-5 flex items-center justify-center"
    style={{ color: active ? '#C9A84C' : '#555555' }}
  >
    {children}
  </span>
);

const GripIcon = () => (
  <svg width="10" height="16" viewBox="0 0 10 16" fill="currentColor" style={{ opacity: 0.35 }}>
    <circle cx="3" cy="2" r="1.2" /><circle cx="7" cy="2" r="1.2" />
    <circle cx="3" cy="6" r="1.2" /><circle cx="7" cy="6" r="1.2" />
    <circle cx="3" cy="10" r="1.2" /><circle cx="7" cy="10" r="1.2" />
  </svg>
);

const SectionLabel = ({ children, dragHandleProps, sectionId }) => (
  <div
    data-sidebar-id={sectionId || undefined}
    className="px-3 mt-5 mb-2 uppercase flex items-center gap-1.5"
    style={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.08em',
      color: 'var(--sidebar-text-sec)',
    }}
  >
    {dragHandleProps && (
      <span
        {...dragHandleProps}
        className="flex items-center cursor-grab active:cursor-grabbing touch-manipulation"
        style={{ color: 'var(--sidebar-text-sec)', lineHeight: 0, marginLeft: -2 }}
      >
        <GripIcon />
      </span>
    )}
    <span className="flex-1 min-w-0 truncate">{children}</span>
  </div>
);

function SortableSectionWrapper({ id, children }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: 'relative',
    zIndex: isDragging ? 50 : 'auto',
    opacity: isDragging ? 0.85 : 1,
    boxShadow: isDragging ? '0 8px 24px rgba(0,0,0,0.35)' : 'none',
    borderRadius: isDragging ? 8 : 0,
    background: isDragging ? 'var(--sidebar-bg)' : 'transparent',
  };

  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      {typeof children === 'function'
        ? children({ dragHandleProps: listeners })
        : children}
    </div>
  );
}

const NavButton = ({ active, onClick, icon, children, badge, itemId }) => (
  <button
    type="button"
    onClick={onClick}
    data-sidebar-id={itemId || undefined}
    className="w-full flex items-center gap-3 px-3 transition text-left touch-target"
    style={{
      minHeight: 44,
      padding: '10px 12px',
      background: 'transparent',
      color: active ? '#C9A84C' : 'var(--sidebar-text)',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.color = '#C9A84C'; }}
    onMouseLeave={(e) => { if (!active) e.currentTarget.style.color = 'var(--sidebar-text)'; }}
  >
    <NavIcon active={active}>{icon}</NavIcon>
    <span className="text-[13px] font-semibold tracking-tight flex-1 min-w-0 truncate">{children}</span>
    {typeof badge === 'number' && (
      <span className="text-[11px] font-bold tabular-nums" style={{ color: 'var(--sidebar-text-sec)' }}>{badge}</span>
    )}
  </button>
);

// ── Icon registry ────────────────────────────────────────────────────

const SERVICE_ICONS = {
  qobuz: <QobuzIcon />,
  prestomusic: <PrestoMusicIcon />,
  local: <span className="text-base">📁</span>,
  live_radio: <span className="text-base">📡</span>,
  ella_radio: <EllaRadioIcon />,
  octave_radio: <span className="text-sky-400"><GramophoneIcon /></span>,
};

const ITEM_ICONS = {
  settings: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
  home: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
      <polyline points="9 22 9 12 15 12 15 22" />
    </svg>
  ),
  devices: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <rect x="4" y="2" width="16" height="20" rx="2" ry="2" />
      <circle cx="12" cy="14" r="4" />
      <line x1="12" y1="6" x2="12.01" y2="6" />
    </svg>
  ),
  prism: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polygon points="12 2 2 22 22 22" />
      <line x1="7" y1="22" x2="2" y2="12" opacity="0.5" />
      <line x1="12" y1="22" x2="4" y2="6" opacity="0.5" />
      <line x1="17" y1="22" x2="8" y2="4" opacity="0.5" />
    </svg>
  ),
  genres: <span className="text-base">🏷️</span>,
  support: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M18 18.72a9.094 9.094 0 003.741-7.347C21.741 5.562 17.377 1 12 1S2.259 5.562 2.259 11.373A9.094 9.094 0 006 18.72m-1.5 2.28a11.35 11.35 0 007.5 3 11.35 11.35 0 007.5-3" />
      <circle cx="12" cy="11" r="4" />
    </svg>
  ),
  account: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
      <circle cx="12" cy="7" r="4" />
    </svg>
  ),
  history: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  ),
  valet_for_you: <span className="text-amber-400"><ValetIcon size={18} /></span>,
  valet_trending: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
      <polyline points="17 6 23 6 23 12" />
    </svg>
  ),
  valet_new_releases: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="10" />
      <polygon points="10 8 16 12 10 16 10 8" fill="currentColor" stroke="none" />
    </svg>
  ),
  valet_community: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  ),
  valet_personalize: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  ),
  lib_albums: <span className="text-base">💿</span>,
  lib_library: <span className="text-base">📚</span>,
  lib_artists: <span className="text-base">🎤</span>,
  lib_tracks: (
    <span className="w-6 h-6 rounded-lg bg-emerald-500/20 border border-emerald-400/20 flex items-center justify-center">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M10 18a2 2 0 1 1-1-1.73V6l12-2v10.5" stroke="#6EE7B7" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M22 15a2 2 0 1 1-1-1.73V4" stroke="#6EE7B7" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  ),
  favorites: (
    <span className="w-6 h-6 rounded-lg bg-rose-500/20 border border-rose-400/20 flex items-center justify-center">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" stroke="#FDA4AF" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  ),
  lib_composers: (
    <span className="w-6 h-6 rounded-lg bg-amber-500/20 border border-amber-400/20 flex items-center justify-center">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M9 18a2 2 0 1 1-1-1.73V5l10-2v11.5" stroke="#FCD34D" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M19 15a2 2 0 1 1-1-1.73V3" stroke="#FCD34D" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
  ),
  lib_my_live_radio: (
    <span className="w-6 h-6 rounded-lg bg-fuchsia-500/20 border border-fuchsia-400/20 flex items-center justify-center">
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <path d="M6 9h12a2 2 0 0 1 2 2v6a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-6a2 2 0 0 1 2-2z" stroke="#F5D0FE" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M8 13h4" stroke="#F5D0FE" strokeWidth="2.2" strokeLinecap="round" />
        <path d="M16 13a2 2 0 1 1 0 4a2 2 0 0 1 0-4z" stroke="#F5D0FE" strokeWidth="2.2" strokeLinejoin="round" />
      </svg>
    </span>
  ),
  lib_folders: <span className="text-base">📂</span>,
  local_library: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
      <path d="M12 7v6" /><path d="M9 10h6" />
    </svg>
  ),
  stream_playlists: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
    </svg>
  ),
  stream_albums: <span className="text-base">💿</span>,
  stream_artists: <span className="text-base">🎤</span>,
  lib_genres: <span className="text-base">🏷️</span>,
  lib_add_music: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
    </svg>
  ),
  lib_manage: (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.6 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.6a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  ),
};

function getIcon(item) {
  if (item.type === 'service') return SERVICE_ICONS[item.id] || null;
  return ITEM_ICONS[item.id] || null;
}

// ── Main component ───────────────────────────────────────────────────

export default function Sidebar({
  activeView,
  libraryTab,
  deviceName,
  connected,
  services,
  onNavigateHome,
  onNavigateGenres,
  onNavigateLibraryTab,
  onNavigateLocalLibrary,
  onOpenServiceAuth,
  onOpenSettings,
  onOpenAuth,
  playlists,
  onOpenPlaylist,
  onNavigateValet,
  onNavigateCommunity,
  onNavigateFavorites,
  onNavigateHistory,
  onNavigateDevices,
  onNavigatePrism,
  onNavigateSupport,
  onNavigateAccount,
  onNavigateAudiophilePlaylists,
  onNavigateGenrePlaylists,
  onNavigateMoodPlaylists,
  onNavigateSeasonPlaylists,
  onNavigateMyPlaylists,
  onNavigateSmartPlaylists,
  onNavigateFavoritePlaylists,
  onNavigateStreamAlbums,
  onNavigateStreamArtists,
  onSearchPlaylistSelect,
  sidebarPinned = false,
  onToggleSidebarPinned,
  onCloseSidebar,
  docked = false,
}) {
  const [showFullFeaturedModal, setShowFullFeaturedModal] = useState(false);
  const desktopMode = useDesktopMode();
  const setDesktopMode = useMaestroStore((s) => s.setDesktopMode);
  const activeZone = useMaestroStore((s) => s.discovery?.activeZone);
  const { sections } = useSidebarConfig({ services });
  const { t } = useTranslation();
  const { sortableIds, updateOrder, resetOrder, applySectionOrder } = useSidebarOrder();
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { distance: 5 } }),
  );

  const displayDeviceName = (desktopMode === 'full' && activeZone?.name)
    ? activeZone.name
    : deviceName;

  // ── Playlist search state ───────────────────────────────────────────
  const [plSearchOpen, setPlSearchOpen] = useState(false);
  const [plQuery, setPlQuery] = useState('');
  const [showSettingsArrow, setShowSettingsArrow] = useState(() => {
    return !localStorage.getItem('maestro_settings_arrow_dismissed');
  });
  const [plResultGroups, setPlResultGroups] = useState([]);
  const [plLoading, setPlLoading] = useState(false);
  const [plListening, setPlListening] = useState(false);
  const plInputRef = useRef(null);
  const recognitionRef = useRef(null);

  const handlePlSearch = useCallback(async (q) => {
    const term = (q || plQuery).trim();
    if (!term) return;
    setPlLoading(true);
    try {
      const res = await playlistService.search(term, 15);
      const newGroup = { query: term, playlists: res.playlists || [] };
      setPlResultGroups(prev => [newGroup, ...prev].slice(0, 3));
    } catch {
      setPlResultGroups(prev => [{ query: term, playlists: [] }, ...prev].slice(0, 3));
    }
    setPlQuery('');
    setPlLoading(false);
  }, [plQuery]);

  const startPlVoice = useCallback(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) { plInputRef.current?.focus(); return; }
    if (plListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setPlListening(false);
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'en-US';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;
    recognitionRef.current = recognition;
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setPlQuery(transcript);
      setPlListening(false);
      handlePlSearch(transcript);
    };
    recognition.onerror = () => setPlListening(false);
    recognition.onend = () => setPlListening(false);
    recognition.start();
    setPlListening(true);
  }, [plListening, handlePlSearch]);

  const openPlSearch = useCallback(() => {
    setPlSearchOpen(true);
    setPlQuery('');
    setTimeout(() => startPlVoice(), 100);
  }, [startPlVoice]);

  const closePlSearch = useCallback(() => {
    setPlSearchOpen(false);
    setPlQuery('');
    if (plListening && recognitionRef.current) {
      recognitionRef.current.stop();
      setPlListening(false);
    }
  }, [plListening]);

  const svc = services || {};
  const playlistItems = Array.isArray(playlists) ? playlists : [];

  let valetName = 'Ella';
  try {
    const stored = localStorage.getItem('maestro_valet_prefs') || localStorage.getItem('maestro_valet_prefs_guest') || '{}';
    const prefs = JSON.parse(stored);
    if (prefs.valetName) valetName = prefs.valetName;
  } catch {}

  // ── Resolve which item is active ───────────────────────────────────

  function isItemActive(item) {
    if (item.id === 'home') {
      // Exact match only — prevent Home from lighting up alongside other items
      return activeView === 'home';
    }
    if (item.id === 'genres') return activeView === 'genres';
    if (item.id === 'history') return activeView === 'history';
    if (item.id === 'devices') return activeView === 'devices';
    if (item.id === 'prism') return activeView === 'prism';
    if (item.id === 'support') return activeView === 'support';
    if (item.id === 'account') return activeView === 'account';
    if (item.id === 'favorites') return activeView === 'favorites';
    if (item.id === 'stream_playlists') return activeView === 'my_playlists';
    if (item.id === 'stream_albums') return activeView === 'stream_albums' || activeView === 'album_detail';
    if (item.id === 'stream_artists') return activeView === 'stream_artists' || activeView === 'artist_detail';
    if (item.id === 'settings') return false;
    if (item.id === 'qobuz') return activeView === 'qobuz' || activeView === 'qobuz_browse';
    if (item.type === 'service') return activeView === item.id;
    if (item.id.startsWith('valet_')) return activeView === item.id;
    if (item.localLibraryTab) {
      return activeView === 'local_library' || activeView === 'local_album_detail';
    }
    if (item.libraryTab) return activeView === 'grid' && libraryTab === item.libraryTab;
    return false;
  }

  // ── Handle clicks ──────────────────────────────────────────────────

  function onItemClick(item) {
    if (item.id === 'settings') {
      if (showSettingsArrow) {
        localStorage.setItem('maestro_settings_arrow_dismissed', 'true');
        setShowSettingsArrow(false);
      }
      onOpenSettings?.();
      return;
    }
    if (item.id === 'home') { onNavigateHome?.(); return; }
    if (item.id === 'genres') { onNavigateGenres?.(); return; }
    if (item.id === 'history') { onNavigateHistory?.(); return; }
    if (item.id === 'devices') { onNavigateDevices?.(); return; }
    if (item.id === 'prism') { onNavigatePrism?.(); return; }
    if (item.id === 'support') { onNavigateSupport?.(); return; }
    if (item.id === 'account') { onNavigateAccount?.(); return; }
    if (item.id === 'favorites') { onNavigateFavorites?.(); return; }
    if (item.type === 'service') { onOpenServiceAuth?.(item.id); return; }
    if (item.id === 'valet_community') { onNavigateCommunity?.(); return; }
    if (item.id.startsWith('valet_')) {
      const sub = item.id.replace('valet_', '');
      onNavigateValet?.(sub);
      return;
    }
    if (item.id === 'stream_playlists') { onNavigateMyPlaylists?.(); return; }
    if (item.id === 'stream_albums') { onNavigateStreamAlbums?.(); return; }
    if (item.id === 'stream_artists') { onNavigateStreamArtists?.(); return; }
    if (item.localLibraryTab) { onNavigateLocalLibrary?.(item.localLibraryTab); return; }
    if (item.libraryTab) { onNavigateLibraryTab?.(item.libraryTab); return; }
  }

  // ── Render a service row (with connection dot) ─────────────────────

  function getServiceDotColor(id) {
    const s = svc?.[id] || {};
    if (s.connected) return 'var(--sidebar-dot-connected)';
    if (s.active && id === 'qobuz') return 'var(--sidebar-dot-sample)';
    if (s.active) return 'var(--sidebar-dot-connected)';
    return 'var(--sidebar-dot-disconnected)';
  }

  function renderServiceItem(item) {
    const isActive = isItemActive(item);
    return (
      <NavButton
        key={item.id}
        active={isActive}
        onClick={() => onItemClick(item)}
        icon={getIcon(item)}
        itemId={item.id}
      >
        <span className="flex items-center gap-2">
          <span className="flex-1">{item.label}</span>
          <span
            className="inline-block w-2 h-2 rounded-full"
            style={{ width: 8, height: 8, background: getServiceDotColor(item.id) }}
          />
        </span>
      </NavButton>
    );
  }

  // ── Render a standard nav item ─────────────────────────────────────

  let prismPower = false;
  try {
    const ps = localStorage.getItem('maestro-prism-state');
    if (ps) prismPower = JSON.parse(ps)?.state?.power ?? false;
  } catch { /* ignore */ }

  function renderNavItem(item) {
    if (item.type === 'service') return renderServiceItem(item);
    if (item.id === 'prism') {
      return (
        <NavButton
          key={item.id}
          active={isItemActive(item)}
          onClick={() => onItemClick(item)}
          icon={getIcon(item)}
          itemId={item.id}
        >
          <span className="flex items-center gap-2">
            <span className="flex-1">{item.label}</span>
            {prismPower && (
              <span
                className="inline-block w-2 h-2 rounded-full"
                style={{ width: 8, height: 8, background: '#00e4b8' }}
              />
            )}
          </span>
        </NavButton>
      );
    }
    if (item.id === 'settings' && showSettingsArrow) {
      return (
        <div key={item.id} style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <div style={{
            position: 'absolute', left: -28, top: '50%', transform: 'translateY(-50%)',
            animation: 'settingsArrowBlink 1s ease-in-out infinite',
            color: '#C9A84C', fontSize: 22, lineHeight: 1, pointerEvents: 'none',
            zIndex: 10,
          }}>
            &#x25B6;
          </div>
          <div style={{ flex: 1 }}>
            <NavButton
              active={isItemActive(item)}
              onClick={() => onItemClick(item)}
              icon={getIcon(item)}
              itemId={item.id}
            >
              {item.label}
            </NavButton>
          </div>
          <style>{`@keyframes settingsArrowBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0.2; } }`}</style>
        </div>
      );
    }
    return (
      <NavButton
        key={item.id}
        active={isItemActive(item)}
        onClick={() => onItemClick(item)}
        icon={getIcon(item)}
        itemId={item.id}
      >
        {item.i18nKey ? t(item.i18nKey, { valetName }) : item.label.replace('{valetName}', valetName)}
      </NavButton>
    );
  }

  // ── Render the Playlists section (custom layout) ───────────────────

  function renderPlaylistsSection(dragHandleProps) {
    return (
      <React.Fragment key="playlists">
        <div className="flex items-center justify-between px-3 mt-5 mb-2">
          <span
            className="uppercase flex items-center gap-1.5"
            style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', color: 'var(--sidebar-text-sec)' }}
          >
            {dragHandleProps && (
              <span
                {...dragHandleProps}
                className="flex items-center cursor-grab active:cursor-grabbing touch-manipulation"
                style={{ color: 'var(--sidebar-text-sec)', lineHeight: 0, marginLeft: -2 }}
              >
                <GripIcon />
              </span>
            )}
            {t('nav.playlists')}
          </span>
          <button
            type="button"
            data-guide-id="smart-playlist-search"
            onClick={plSearchOpen ? closePlSearch : openPlSearch}
            className="flex items-center justify-center transition-colors"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: plSearchOpen ? '#C9A84C' : 'var(--sidebar-text-sec)' }}
            title="Search playlists"
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </button>
        </div>

        {plSearchOpen && (
          <div className="px-3 mb-1">
            <form
              onSubmit={(e) => { e.preventDefault(); handlePlSearch(); }}
              className="flex items-center gap-1.5 rounded-lg px-2.5 py-1.5"
              style={{ background: 'var(--sidebar-border)', border: '1px solid var(--sidebar-border)' }}
            >
              <input
                ref={plInputRef}
                type="text"
                value={plQuery}
                onChange={(e) => setPlQuery(e.target.value)}
                placeholder={plListening ? 'Listening...' : 'Search playlists...'}
                className="flex-1 bg-transparent text-[13px] outline-none min-w-0"
                style={{ color: 'var(--sidebar-text)', border: 'none' }}
              />
              <button
                type="button"
                onClick={startPlVoice}
                className="flex items-center justify-center"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: plListening ? '#ef4444' : 'var(--sidebar-text-sec)' }}
                title={plListening ? 'Stop listening' : 'Voice search'}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill={plListening ? '#ef4444' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
                  <path d="M19 10v2a7 7 0 0 1-14 0v-2" /><line x1="12" y1="19" x2="12" y2="23" /><line x1="8" y1="23" x2="16" y2="23" />
                </svg>
              </button>
              <button
                type="submit"
                className="flex items-center justify-center"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: '#C9A84C' }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </form>
            {plLoading && (
              <div className="py-2 text-center text-[11px]" style={{ color: 'var(--sidebar-text-sec)' }}>Searching...</div>
            )}
          </div>
        )}

        {plResultGroups.length > 0 && (
          <div className="px-3 mb-2">
            <div className="max-h-[340px] overflow-y-auto no-scrollbar rounded-lg" style={{ background: 'var(--sidebar-border)' }}>
              {plResultGroups.map((group, gi) => (
                <div key={`${group.query}-${gi}`}>
                  <div className="px-2.5 pt-2 pb-1 flex items-center justify-between" style={{ borderTop: gi > 0 ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                    <span className="text-[10px] font-bold uppercase tracking-wider" style={{ color: '#C9A84C' }}>
                      &ldquo;{group.query}&rdquo;
                    </span>
                    {gi === 0 && (
                      <button
                        type="button"
                        onClick={() => setPlResultGroups([])}
                        className="text-[9px]"
                        style={{ color: 'var(--sidebar-text-sec)', background: 'none', border: 'none', cursor: 'pointer' }}
                      >Clear all</button>
                    )}
                  </div>
                  {group.playlists.length === 0 && (
                    <div className="px-2.5 pb-2 text-[11px]" style={{ color: 'var(--sidebar-text-sec)' }}>No results</div>
                  )}
                  {group.playlists.map((p) => {
                    const pid = p.servicePlaylistId || p.id;
                    const img = p.imageUrl || (p.images300 && p.images300[0]) || (p.images150 && p.images150[0]) || null;
                    return (
                      <button
                        key={`${gi}-${pid}`}
                        type="button"
                        onClick={() => {
                          const navId = String(pid).replace(/^qobuz:playlist:/, '');
                          onSearchPlaylistSelect?.(navId);
                        }}
                        className="w-full flex items-center gap-2.5 px-2.5 py-2 text-left hover:brightness-125 transition"
                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', borderBottom: '1px solid rgba(255,255,255,0.04)' }}
                      >
                        {img && (
                          <img src={img} alt="" className="w-9 h-9 rounded object-cover flex-shrink-0" />
                        )}
                        <div className="min-w-0 flex-1">
                          <div className="text-[12px] font-semibold truncate" style={{ color: 'var(--sidebar-text)' }}>{p.name}</div>
                          <div className="text-[10px] truncate" style={{ color: 'var(--sidebar-text-sec)' }}>
                            {p.owner || 'Qobuz'}{p.trackCount ? ` · ${p.trackCount} tracks` : ''}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        )}

        <NavButton
          active={activeView === 'smart_playlists' || activeView === 'valet_for_you'}
          onClick={() => onNavigateSmartPlaylists?.()}
          itemId="valet_for_you"
          icon={<span className="text-amber-400"><ValetIcon size={18} /></span>}
        >
          {t('nav.valet_picks', { valetName })}
        </NavButton>
        <NavButton
          active={activeView === 'my_playlists' || activeView === 'favorites' || activeView === 'favorites_playlists'}
          onClick={() => onNavigateMyPlaylists?.()}
          itemId="my_playlists"
          icon={
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18V5l12-2v13" /><circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
            </svg>
          }
        >
          {t('nav.my_playlists')}
        </NavButton>
        <NavButton
          active={activeView === 'audiophile_playlists'}
          onClick={() => onNavigateAudiophilePlaylists?.()}
          itemId="audiophile_playlists"
          icon={<span className="text-base">🎧</span>}
        >
          {t('nav.audiophile')}
        </NavButton>
        <NavButton
          active={activeView === 'genre_playlists'}
          onClick={() => onNavigateGenrePlaylists?.()}
          itemId="genre_playlists"
          icon={<span className="text-base">🏷️</span>}
        >
          {t('nav.genre_playlists')}
        </NavButton>
        <NavButton
          active={activeView === 'mood_playlists'}
          onClick={() => onNavigateMoodPlaylists?.()}
          itemId="mood_playlists"
          icon={<span className="text-base">🌙</span>}
        >
          {t('nav.mood_playlists')}
        </NavButton>
        <NavButton
          active={activeView === 'season_playlists'}
          onClick={() => onNavigateSeasonPlaylists?.()}
          itemId="season_playlists"
          icon={<span className="text-base">🍂</span>}
        >
          {t('nav.season_playlists')}
        </NavButton>
      </React.Fragment>
    );
  }

  // ── Render a section ───────────────────────────────────────────────

  function renderSection(section, { dragHandleProps } = {}) {
    if (section.id === 'playlists') return renderPlaylistsSection(dragHandleProps);

    const label = section.i18nKey
      ? t(section.i18nKey, { valetName })
      : (section.label || '').replace('{valetName}', valetName);
    return (
      <React.Fragment key={section.id}>
        {label ? <SectionLabel dragHandleProps={dragHandleProps} sectionId={section.id}>{label}</SectionLabel> : null}
        {section.items.map(renderNavItem)}
      </React.Fragment>
    );
  }

  // ── Layout ─────────────────────────────────────────────────────────

  const PinIcon = ({ filled }) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill={filled ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M12 17v5" />
      <path d="M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V16a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V7a1 1 0 0 0-1-1h-4a1 1 0 0 0-1 1v3.76z" />
    </svg>
  );

  return (
    <>
    <aside
      className="w-full flex-shrink-0 h-full backdrop-blur-2xl"
      style={{
        background: 'var(--sidebar-bg)',
        borderRight: '1px solid var(--sidebar-border)',
        color: 'var(--sidebar-text)',
      }}
    >
      <div className="h-full flex flex-col">
        <div className="px-4 pt-5 pb-2" style={{ paddingTop: 'max(20px, env(safe-area-inset-top, 20px))' }}>
          <div className="flex items-start justify-between gap-2 w-full">
            <div className="flex flex-col items-start min-w-0">
              <button type="button" onClick={() => { onNavigateHome?.(); }} style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer' }}>
                <img
                  src={maestroLogo}
                  alt="Maestro"
                  className="w-20 mb-2 rounded-2xl"
                />
              </button>
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em]" style={{ color: 'var(--sidebar-text-sec)' }}>
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ background: connected ? '#22c55e' : '#ef4444' }}
                />
              </div>
            </div>
            {!docked && onCloseSidebar && (
              <button
                type="button"
                onClick={onCloseSidebar}
                className="flex-shrink-0 rounded-lg flex items-center justify-center"
                style={{
                  width: 32,
                  height: 32,
                  border: '1px solid var(--sidebar-border)',
                  background: 'rgba(255,255,255,0.04)',
                  color: 'var(--sidebar-text-sec)',
                }}
                title={t('sidebar.close')}
                aria-label={t('sidebar.close')}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M18 6L6 18" /><path d="M6 6l12 12" />
                </svg>
              </button>
            )}
          </div>
        </div>

        <div className="mb-2" style={{ borderBottom: '1px solid var(--sidebar-border)' }}>
          <AccountMenu onOpenSettings={onOpenSettings} onOpenAuth={onOpenAuth} />
        </div>

        <nav className="px-3 pb-40 overflow-y-auto no-scrollbar ios-scroll flex-1">
          {(() => {
            const topNav = sections.find(s => s.id === 'top_nav');
            const orderedSections = applySectionOrder(sections);
            const sortableSections = orderedSections.filter(s => s.id !== 'top_nav');

            const handleDragEnd = (event) => {
              const { active, over } = event;
              if (!over || active.id === over.id) return;
              const oldIndex = sortableSections.findIndex(s => s.id === active.id);
              const newIndex = sortableSections.findIndex(s => s.id === over.id);
              if (oldIndex === -1 || newIndex === -1) return;
              const reordered = arrayMove(
                sortableSections.map(s => s.id),
                oldIndex,
                newIndex,
              );
              updateOrder(reordered);
            };

            return (
              <>
                {topNav && renderSection(topNav)}
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={sortableSections.map(s => s.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {sortableSections.map(section => (
                      <SortableSectionWrapper key={section.id} id={section.id}>
                        {({ dragHandleProps }) => renderSection(section, { dragHandleProps })}
                      </SortableSectionWrapper>
                    ))}
                  </SortableContext>
                </DndContext>
                <button
                  type="button"
                  onClick={resetOrder}
                  className="w-full text-center mt-4 mb-2 transition-colors hover:text-white/60"
                  style={{
                    fontSize: 10,
                    color: 'var(--sidebar-text-sec)',
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    letterSpacing: '0.04em',
                  }}
                >
                  Reset section order
                </button>
              </>
            );
          })()}
        </nav>

        {onToggleSidebarPinned && (
          <div
            className="flex-shrink-0 px-3 pb-3"
            style={{
              paddingBottom: 'max(12px, env(safe-area-inset-bottom, 12px))',
              borderTop: '1px solid var(--sidebar-border)',
              background: 'var(--sidebar-bg)',
            }}
          >
            <button
              type="button"
              data-guide-id="sidebar-pin-toggle"
              onClick={onToggleSidebarPinned}
              className="w-full flex items-center gap-3 text-left transition active:scale-[0.98]"
              style={{
                padding: '10px 12px',
                borderRadius: 12,
                border: `1px solid ${sidebarPinned ? 'rgba(201,168,76,0.45)' : 'var(--sidebar-border)'}`,
                background: sidebarPinned ? 'rgba(201,168,76,0.12)' : 'rgba(255,255,255,0.03)',
                color: sidebarPinned ? '#C9A84C' : 'var(--sidebar-text)',
              }}
              aria-pressed={sidebarPinned}
              title={sidebarPinned ? t('sidebar.pinnedHint') : t('sidebar.pinOpenHint')}
            >
              <span className="flex-shrink-0" style={{ color: sidebarPinned ? '#C9A84C' : 'var(--sidebar-text-sec)' }}>
                <PinIcon filled={sidebarPinned} />
              </span>
              <span className="flex-1 min-w-0">
                <span className="block text-[12px] font-semibold leading-tight">
                  {sidebarPinned ? t('sidebar.pinned') : t('sidebar.pinOpen')}
                </span>
                <span className="block text-[10px] leading-snug mt-0.5" style={{ color: 'var(--sidebar-text-sec)' }}>
                  {sidebarPinned ? t('sidebar.pinnedHint') : t('sidebar.pinOpenHint')}
                </span>
              </span>
              <span
                className="flex-shrink-0 relative rounded-full transition-colors"
                style={{
                  width: 38,
                  height: 22,
                  background: sidebarPinned ? '#C9A84C' : 'rgba(255,255,255,0.15)',
                }}
                aria-hidden="true"
              >
                <span
                  className="absolute top-0.5 rounded-full bg-white shadow transition-transform"
                  style={{
                    width: 18,
                    height: 18,
                    left: sidebarPinned ? 18 : 2,
                  }}
                />
              </span>
            </button>
          </div>
        )}
      </div>
    </aside>
    </>
  );
}
